<template>
    <div
        role="img"
        :aria-label="computedAriaLabel"
        :class="[$style['mds-star-rating'], sizeClass, classObject]"
        :data-mds-version="version"
    >
        <mds-icon
            v-for="(star, index) in rating"
            :key="index"
            name="star-rating-star"
            :style="customSizeStyle"
            :class="$style['mds-star-rating__star']"
        ></mds-icon>
        <mds-icon
            v-if="quantitative"
            :style="customSizeStyle"
            :class="$style['mds-star-rating__quant']"
            name="star-rating-quant"
        ></mds-icon>
    </div>
</template>

<script>
    import MdsIcon from '@mds/icon';
    import packageInfo from '../package.json';
    const { name, version } = packageInfo;

    MdsIcon.customIcons['star-rating-quant'] =
        '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 15 15"><path stroke="none" d="M7.87038,6.56324l-.906-.91975a3.35258,3.35258,0,0,0,.856-2.25042A3.41025,3.41025,0,0,0,1,3.39307,3.36428,3.36428,0,0,0,4.40949,6.78614,3.43851,3.43851,0,0,0,6.38676,6.1656l.92831.94313ZM4.40949,6.02586A2.61451,2.61451,0,0,1,1.811,3.39307,2.57109,2.57109,0,0,1,4.40949.75957a2.57141,2.57141,0,0,1,2.59995,2.6335,2.61676,2.61676,0,0,1-.60983,1.67717L4.86388,3.51131l-.55255.54549L5.82726,5.59709A2.57174,2.57174,0,0,1,4.40949,6.02586Z"/></svg>';

    MdsIcon.customIcons['star-rating-star'] =
        '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 15 15"><path stroke="none" d="M10.37 9.192l1.781 5.541-4.661-3.415-4.609 3.415 1.8-5.541L0 5.7h5.751L7.49.267 9.228 5.7H15z"/></svg>';

    export default {
        name: 'MdsStarRating',
        components: {
            MdsIcon,
        },
        data() {
            return {
                version: `${name}-${version}`,
            };
        },
        props: {
            customSize: {
                type: Number,
                docs: {
                    validation: '—',
                    description:
                        'Sets a custom height for the component in pixels. Setting a `customSize` will always override `size`.',
                },
            },
            onDark: {
                type: Boolean,
                default: false,
                docs: {
                    validation: '—',
                    description: 'If `true`, changes component to on-dark styling.',
                },
            },
            quantitative: {
                type: Boolean,
                default: false,
                docs: {
                    validation: '—',
                    description: 'If `true`, renders the Q icon, indicating the rating is quantitative.',
                },
            },
            rating: {
                type: Number,
                validator(value) {
                    return value >= 1 && value <= 5;
                },
                docs: {
                    validation: 'Must be a number between 1 and 5',
                    description: 'Sets the number of stars.',
                },
            },
            size: {
                type: String,
                default: 'medium',
                validator(value) {
                    return ['small', 'medium', 'large'].indexOf(value) !== -1;
                },
                docs: {
                    validation: 'One of `small`, `medium`, `large`.',
                    description: 'Sets the size of the component.',
                },
            },
        },
        computed: {
            sizeClass() {
                return this.$style[`mds-star-rating--${this.size}`];
            },
            classObject() {
                const obj = {};
                obj[this.$style['mds-component--on-dark']] = this.onDark;

                return obj;
            },
            quantLabel() {
                return this.quantitative ? 'quantitative ' : '';
            },
            computedAriaLabel() {
                return `${this.quantLabel}rating of ${this.rating} out of 5 stars`;
            },
            customSizeStyle() {
                return this.customSize ? `height: ${this.customSize}px; width: ${this.customSize}px;` : null;
            },
        },
    };
</script>

<style lang="scss" module>
    @import './star-rating.scss';
</style>
